import CircleGeom from 'ol/geom/Circle';
import type Point from 'ol/geom/Point';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';

import { isNonEmptyString } from '@nora-monorepo/shared';

import { SymbolStyleManager } from '#nora-ui/map/drawtools/styles/symbol-style';
import { FeatureStyleManager } from '#nora-ui/map/map/FeatureStyleManager';

const MARKER_COLOR = '#00C3FF';
const ACCURACY_FILL_COLOR = '#36B8B866'; // 40% opacity

export function getAccuracyStyle(accuracy: number): Style {
  return new Style({
    zIndex: 998,
    fill: new Fill({ color: ACCURACY_FILL_COLOR }),
    geometry: (feature) => {
      const point = feature.getGeometry() as Point;
      const center = point.getCoordinates();

      return new CircleGeom(center, accuracy);
    },
  });
}

export function getHeadingStyle(
  headingDegrees: number | null | undefined,
): Style[] {
  if (headingDegrees == null) {
    return [];
  }

  const BASE_HEIGHT_PX = 38; // Visual height of the wedge
  const BASE_ASPECT_RATIO = 26 / 38;
  const BASE_START_WIDTH_RATIO = 0.28; // How wide the wedge is near the tip (as fraction of width)
  const BASE_BASE_CURVE_PX = 6; // How rounded the bottom is
  const BASE_TIP_CURVE_RATIO = 0.35; // How curved the top is
  const BASE_BORDER_THICKNESS_PX = 2; // Border thickness near the center
  const BORDER_OPACITY_START = 0.65; // Opacity of border near the center
  const BORDER_OPACITY_END = 0; // Opacity of border at the far end
  const GRADIENT_OPACITY_START = 1; // Fill opacity near the center
  const GRADIENT_OPACITY_END = 0; // Fill opacity at the far end

  const heightPx = BASE_HEIGHT_PX;
  const widthPx = Math.round(BASE_ASPECT_RATIO * heightPx);
  // Convert heading to radians and rotate 180 degrees to point "up"
  const rotationRadians = (headingDegrees * Math.PI) / 180 + Math.PI;

  const style = new Style({
    zIndex: 999,
    renderer: (coords, state) => {
      const ctx = state.context;
      const pixelRatio = state.pixelRatio ?? 1;

      // Scale for HiDPI
      const x = coords[0] as number;
      const y = coords[1] as number;
      const h = heightPx * pixelRatio;
      const halfWEnd = (widthPx / 2) * pixelRatio;
      const halfWStart = Math.max(4 * pixelRatio, widthPx * BASE_START_WIDTH_RATIO * pixelRatio);
      const baseCurve = BASE_BASE_CURVE_PX * pixelRatio;
      const tipCurve = halfWStart * BASE_TIP_CURVE_RATIO;
      const borderBase = BASE_BORDER_THICKNESS_PX * pixelRatio;

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotationRadians);

      // Fill (gradient: solid near center -> transparent at end)
      const fillGrad = ctx.createLinearGradient(0, 0, 0, h);
      fillGrad.addColorStop(0, `rgba(0,195,255,${GRADIENT_OPACITY_START})`);
      fillGrad.addColorStop(1, `rgba(0,195,255,${GRADIENT_OPACITY_END})`);

      ctx.beginPath();
      ctx.moveTo(-halfWStart, 0); // Left mouth at center
      ctx.lineTo(-halfWEnd, h - baseCurve); // Left edge
      ctx.quadraticCurveTo(0, h + 2 * pixelRatio, halfWEnd, h - baseCurve); // Rounded base
      ctx.lineTo(halfWStart, 0); // Right edge
      ctx.quadraticCurveTo(0, -tipCurve, -halfWStart, 0); // Slight concave top
      ctx.closePath();

      ctx.fillStyle = fillGrad;
      ctx.fill();

      // Tapered side borders (fade to 0 at far end)
      // Left side border polygon
      ctx.beginPath();
      ctx.moveTo(-halfWStart - borderBase, 0);
      ctx.lineTo(-halfWStart, 0);
      ctx.lineTo(-halfWEnd, h - baseCurve);
      ctx.closePath();

      const leftGrad = ctx.createLinearGradient(0, 0, 0, h);
      leftGrad.addColorStop(0, `rgba(0,0,0,${BORDER_OPACITY_START})`);
      leftGrad.addColorStop(1, `rgba(0,0,0,${BORDER_OPACITY_END})`);
      ctx.fillStyle = leftGrad;
      ctx.fill();

      // Right side border polygon (mirror)
      ctx.beginPath();
      ctx.moveTo(halfWStart, 0);
      ctx.lineTo(halfWStart + borderBase, 0);
      ctx.lineTo(halfWEnd, h - baseCurve);
      ctx.closePath();

      const rightGrad = ctx.createLinearGradient(0, 0, 0, h);
      rightGrad.addColorStop(0, `rgba(0,0,0,${BORDER_OPACITY_START})`);
      rightGrad.addColorStop(1, `rgba(0,0,0,${BORDER_OPACITY_END})`);
      ctx.fillStyle = rightGrad;
      ctx.fill();

      ctx.restore();
    },
    geometry: feature => feature.getGeometry(),
  });

  return [style];
}

export function getDotStyleMarker(radiusPx = 7): Style[] {
  // Outer white dot
  const outerRing = new Style({
    zIndex: 999,
    image: new CircleStyle({
      radius: radiusPx + 4,
      fill: new Fill({ color: '#FFFFFF' }),
    }),
  });

  // Inner blue dot, on top of white dot
  const innerDot = new Style({
    zIndex: 1000,
    image: new CircleStyle({
      radius: radiusPx,
      fill: new Fill({ color: MARKER_COLOR }),

    }),
  });

  return [outerRing, innerDot];
}

interface OwnLocationFeatureStyleOptions {
  accuracy: { show: boolean; value: number };
  heading: { show: boolean; value: number };
  symbol?: string;
}

export function getOwnLocationFeatureStyle(options?: OwnLocationFeatureStyleOptions): Style[] {
  const { accuracy, heading } = {
    ...{
      accuracy: { show: false, value: 0 },
      heading: { show: false, value: 0 },
    },
    ...options,
  };

  const markerStyle = getDotStyleMarker();
  const style = [...markerStyle];

  if (accuracy?.show) {
    const accuracyStyle = getAccuracyStyle(accuracy.value);

    style.unshift(accuracyStyle);
  }

  if (heading.show) {
    const headingStyle = getHeadingStyle(heading.value);

    style.unshift(...headingStyle);
  }

  return style;
}

interface LocationFeatureStyleOptions {
  text?: string;
  symbolPackId?: string;
  symbolId?: string;
  onLoad?: () => void;
}

export function getLocationFeatureStyle(options?: LocationFeatureStyleOptions): Style[] {
  const { symbolPackId, symbolId, onLoad } = {
    ...{
      symbolPackId: undefined,
      symbolId: undefined,
    },
    ...options,
  };

  let marker: Style[] | Style = [];

  const isCustomSymbol = isNonEmptyString(symbolPackId) && isNonEmptyString(symbolId);

  if (isCustomSymbol) {
    marker = SymbolStyleManager.getSymbolGraphic({
      symbolId,
      symbolPackId,
      onLoad,
    });
  }
  else {
    marker = FeatureStyleManager.defaultUserIcon();
  }

  return Array.isArray(marker) ? marker : [marker];
}

export function getLocationClusterFeatureStyle({ size }: { size: number }): Style[] {
  let fontSize = '16px';
  let textOffset = -3;
  let iconOffset = 3;

  if (size > 9) {
    textOffset = 0;
    iconOffset = 1;
  }
  if (size > 99) {
    fontSize = '12px';
    textOffset = 2;
    iconOffset = 2;
  }
  if (size > 999) {
    fontSize = '10px';
  }

  return [
    new Style({
      zIndex: 999,
      image: new CircleStyle({
        radius: 24,
        stroke: new Stroke({
          color: '#FFFFFF',
          width: 4,
        }),
        fill: new Fill({
          color: '#092020',
        }),
      }),
      text: new Text({
        fill: new Fill({ color: '#FFFFFF' }),
        font: `700 ${fontSize} Source Sans Pro`,
        text: size.toString(),
        textAlign: 'end',
        offsetX: textOffset,
      }),
    }),
    new Style({
      zIndex: 999,
      text: new Text({
        text: 'person',
        font: '16px "Material Icons"',
        fill: new Fill({ color: '#FFFFFF' }),
        textAlign: 'start',
        offsetX: iconOffset,
      }),
    }),
  ];
}
